import cookie from 'js-cookie';
import { TRACK_VIEW_TRACKING_LINK_START } from '../../store/actions/ActionConstant';

const TRACKING_COOKIE_NAME = 'sf_tracking';
const TRACKING_COOKIE_EXPIRY_DAYS = 30;

/**
 * Extract tracking code from URL query parameter
 * @param {string} search - window.location.search or location.search
 * @returns {string|null} - Tracking code or null
 */
export function getTrackingCodeFromUrl(search) {
  if (!search) return null;
  const params = new URLSearchParams(search);
  return params.get('t') || null;
}

/**
 * Get tracking data from cookie
 * @returns {object|null} - { code, destination_type } or null
 */
export function getTrackingDataFromCookie() {
  try {
    const cookieData = cookie.get(TRACKING_COOKIE_NAME);
    if (!cookieData) return null;
    return JSON.parse(cookieData);
  } catch (error) {
    return null;
  }
}

/**
 * Set tracking data in cookie
 * @param {string} code - Tracking code
 * @param {string} destinationType - 'profile' or 'landing'
 */
export function setTrackingCookie(code, destinationType) {
  const cookieData = {
    code,
    destination_type: destinationType
  };
  cookie.set(TRACKING_COOKIE_NAME, JSON.stringify(cookieData), {
    expires: TRACKING_COOKIE_EXPIRY_DAYS,
    path: '/'
  });
}

/**
 * Get referrer URL, filtering out same-origin referrers
 * @returns {string|null} - External referrer URL or null
 */
function getReferrerUrl() {
  if (typeof document === 'undefined') return null;
  
  const referrer = document.referrer;
  
  // Return null if no referrer or empty string
  if (!referrer) return null;
  
  try {
    // Parse referrer to get its origin
    const referrerUrl = new URL(referrer);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    
    // Filter out same-origin referrers (internal navigation)
    // Only include external referrers for tracking purposes
    if (currentOrigin && referrerUrl.origin === currentOrigin) {
      return null;
    }
    
    return referrer;
  } catch (error) {
    // If referrer is not a valid URL, return null
    // This handles cases where document.referrer might be malformed
    return null;
  }
}

/**
 * Initialize tracking link on page load
 * Should be called when user lands on profile or landing page
 * @param {string} destinationType - 'profile' or 'landing'
 * @param {string} search - window.location.search
 * @param {function} dispatch - Redux dispatch function
 */
export function initializeTrackingLink(destinationType, search, dispatch) {
  const trackingCode = getTrackingCodeFromUrl(search);
  const existingData = getTrackingDataFromCookie();
  
  if (!trackingCode) {
    // No tracking code in URL, return existing cookie data or null
    return existingData;
  }
  
  // Check if the same tracking code already exists in cookie
  if (existingData && existingData.code === trackingCode) {
    // Same tracking code already in cookie - avoid duplicate API call
    return existingData;
  }
  
  // New tracking code found (or different code) - save to cookie
  setTrackingCookie(trackingCode, destinationType);
  
  // Dispatch Redux action to track view event (only for new/different code)
  if (dispatch) {
    const referrerUrl = getReferrerUrl();
    const payload = {
      code: trackingCode,
      destination_type: destinationType,
      ...(referrerUrl && { referrer_url: referrerUrl })
    };
    dispatch({ type: TRACK_VIEW_TRACKING_LINK_START, payload });
  }
  
  return {
    code: trackingCode,
    destination_type: destinationType
  };
}

/**
 * Get tracking data to include in API request payloads
 * Used for login, signup, and subscription events
 * @returns {object} - { tracking_code, destination_type } or empty object
 */
export function getTrackingDataForPayload() {
  const trackingData = getTrackingDataFromCookie();
  if (!trackingData || !trackingData.code) {
    return {};
  }
  return {
    tracking_code: trackingData.code,
    destination_type: trackingData.destination_type
  };
}

